/**
 * プラットフォーム判定ユーティリティ。
 *
 * 目的:
 *   - `Capacitor.getPlatform()` や `window.liff` の直接参照を本ファイルに集約し、
 *     他モジュールからは `getPlatformId()` など意味のある API のみを使わせる。
 *   - SSR / CDN 未ロード / テスト環境でも落ちないよう、`typeof window` などの
 *     ガードを徹底する。
 *
 * Phase 4（T12 認証フロー）拡張:
 *   - Web かつ認証済みのとき 'web_user' を返す。
 *   - `authStateHint` 引数はテスト / 初期化時に状態を与えるためのフック。
 *   - 実行時は `services/auth.ts` の subscribeAuthState で更新された状態を参照する。
 *
 * 参考: ../../luckyanimal-sweeper/src/utils/platform.ts
 */
import { Capacitor } from '@capacitor/core'

/** YamanekoGames Hub SDK に渡すプラットフォーム識別子。 */
export type YmgPlatformId = 'android' | 'ios' | 'line_mini' | 'web_user' | 'web_guest'

/**
 * Capacitor ネイティブアプリ（Android/iOS）上で動作しているかどうか。
 * Web ブラウザ実行時は常に false。
 */
export function isCapacitorNative(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    // Capacitor ランタイム未注入（SSR / ユニットテスト等）では false 扱い。
    // fallback:ok — 「ネイティブ機能は使えない」ことは意味のある真実。
    return false
  }
}

/**
 * LINE Mini App（LIFF SDK）環境で動作しているかどうか。
 * Capacitor ネイティブでは LIFF は使わないので常に false。
 *
 * LINE Client 内（isInClient）だけでなく、外部ブラウザでも LIFF ログイン済みの場合は
 * true を返す（Match3 相当）。これにより PC ブラウザから LIFF URL 経由でログインした
 * ユーザーも `line_mini` プラットフォームとして Hub に認証される。
 */
export function isLineApp(): boolean {
  if (typeof window === 'undefined') return false
  if (isCapacitorNative()) return false
  const w = window as unknown as {
    liff?: { isInClient?: () => boolean; isLoggedIn?: () => boolean }
  }
  if (!w.liff) return false
  try {
    if (w.liff.isInClient?.() === true) return true
    if (w.liff.isLoggedIn?.() === true) return true
    return false
  } catch {
    // LIFF の初期化前に呼ばれると例外になることがある。
    // fallback:ok — 未初期化＝LINE 内ではない、とみなして安全側に倒す。
    return false
  }
}

/**
 * 現在のプラットフォームに対応する YMG platformId を返す。
 *
 * 判定優先度:
 *   1. Capacitor ネイティブ(iOS) → 'ios'
 *   2. Capacitor ネイティブ(Android) → 'android'
 *   3. LINE Mini App (`liff.isInClient()` が true) → 'line_mini'
 *   4. Web ブラウザ + 認証済み → 'web_user'
 *   5. 上記以外 → 'web_guest'
 *
 * 認証状態の参照は循環 import を避けるため lazy（runtime）でのみ行う。
 * ここでは `authProbe` を外から差し込める形にして、services/auth を直接 import しない。
 */
let authProbe: (() => boolean) | null = null

/**
 * 「Web で認証済みか」を判定する関数を登録する。
 * `services/auth.ts` 側で `registerAuthProbe(() => getAuthState().status === 'signed_in')`
 * のように設定される。
 */
export function registerAuthProbe(probe: (() => boolean) | null): void {
  authProbe = probe
}

export function getPlatformId(): YmgPlatformId {
  if (isCapacitorNative()) {
    const platform = Capacitor.getPlatform()
    return platform === 'ios' ? 'ios' : 'android'
  }
  if (isLineApp()) {
    return 'line_mini'
  }
  // Web: 認証済みかどうかで web_user / web_guest を切り替える
  if (authProbe) {
    try {
      if (authProbe()) return 'web_user'
    } catch (err) {
      // fallback:ok — 認証プローブが壊れていてもゲストには落とせる
      console.warn('[platform] authProbe failed:', err)
    }
  }
  return 'web_guest'
}

/**
 * 将来の認証 / デバイス制限 API（`/auth/session` 等）で送信する端末情報。
 *
 * 現状は `platform` のみを返す最小セット。Phase 4 で `@capacitor/device` を
 * 導入した際に `osVersion` / `deviceModel` を埋める（同期でなく async 版を
 * 別途用意する可能性あり）。
 */
export interface DeviceInfo {
  platform: YmgPlatformId
  osVersion?: string
  deviceModel?: string
}

export function getDeviceInfo(): DeviceInfo {
  return {
    platform: getPlatformId(),
  }
}
